import { useEffect, useRef } from 'react';
import { isDashboardLayoutLocked } from '../../Components/Dashboard/dashboardLayoutLock';
import { getMergedDashboardLayoutForStreaming } from '../../Components/Dashboard/persistDashboardWidgetContent';
import { useAppDispatch, useAppSelector } from '../../redux/ReduxHooks';
import { projectSlice } from '../../redux/ProjectSlice';
import { selectIsProgramActive } from '../../redux/RobotSlice';
import { ApplicationActions } from '../../utils/constants';
import {
  getDashboardStreamSyncLayoutSignature,
  isDashboardProgramCodeInSync,
  rebuildDashboardProgramCodeFromLayout,
} from '../../utils/sensor-stream-tracker';

/**
 * Syncs dashboard streaming setup in program code when the layout's required stream keys change:
 * `DataStreamStore.get_instance().set_request_list` and `DataStreamStore.set_streaming_interval`
 * (interval is inserted only when missing).
 *
 * First-time insert wraps the two lines in
 * `#region - Data streaming` / `#endregion`;
 * region markers are not re-added if the user removes them.
 *
 * **Intentional:** After a layout signature has been synced once, manual edits to stream keys
 * are not overwritten until the dashboard layout changes (add/remove widget or different
 * `sensorKeys`) or the loaded project id changes. Layout is the source of truth for which keys
 * belong in code; the editor may diverge on keys until the user changes widgets again.
 *
 * Formatting-only normalization may still run on the first sync for a layout signature when
 * keys already match but `set_request_list` is on one long line. That path is gated by
 * `isDashboardProgramCodeInSync` (not `isStreamingSetupInSync` alone), so entering live mode or
 * loading a project can dispatch one program update to apply Ruff-style formatting before the
 * layout signature is marked synced. Parsed request-list order is preserved during formatting-only
 * rewrites; layout order applies when keys are added or removed. Calls that include user `#`
 * comments are left unchanged.
 */
export function useSyncDataStreamKeysFromLayout() {
  const dispatch = useAppDispatch();
  const loadedProject = useAppSelector((state) => state.project.loadedProject);
  const programState = useAppSelector((state) => state.project.programState);
  const isProgramActive = useAppSelector(selectIsProgramActive);
  const dashboardViewMode = useAppSelector((state) => state.application.dashboardViewMode);
  const projectId = loadedProject?.id;
  const readOnly = loadedProject?.readOnly;
  const programCode = loadedProject?.program?.code;
  const dashboardLayout = loadedProject?.dashboardLayout;
  const syncedKeysSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    syncedKeysSignatureRef.current = null;
  }, [projectId]);

  useEffect(() => {
    if (dashboardViewMode !== ApplicationActions.DashboardViewMode.LIVE) return;
    if (isDashboardLayoutLocked({ programState, isProgramActive })) return;
    if (!projectId || readOnly || programCode == null) return;

    const effectiveLayout = getMergedDashboardLayoutForStreaming(dashboardLayout ?? []);
    const layoutSig = getDashboardStreamSyncLayoutSignature(effectiveLayout);

    if (syncedKeysSignatureRef.current === layoutSig) return;

    if (isDashboardProgramCodeInSync(programCode, effectiveLayout)) {
      syncedKeysSignatureRef.current = layoutSig;
      return;
    }

    const nextCode = rebuildDashboardProgramCodeFromLayout(programCode, effectiveLayout);
    if (nextCode !== programCode) {
      dispatch(projectSlice.actions.onUpdateLastModified(new Date().toISOString()));
      dispatch(projectSlice.actions.onUpdateProgram(nextCode));
    }

    syncedKeysSignatureRef.current = layoutSig;
  }, [dispatch, projectId, readOnly, programCode, dashboardLayout, dashboardViewMode, programState, isProgramActive]);
}
